import React, { useState, useRef, useCallback } from 'react';
import { Image, Upload, X } from 'lucide-react';
import Button from '../../Button';
import './SimpleUpload.css';

/**
 * FileUpload / Simple
 *
 * Pixel-perfect implementation from Figma.
 * States: idle → loading (auto, instantly) → uploaded  |  error
 *
 * Figma node: 8538:52526 (File loading), 8538:53120 (Error / File selected)
 * Figma file: SCDTs7oU1gASxkWViw1rJy
 */
const STATES = {
  IDLE: 'idle',
  LOADING: 'loading',
  UPLOADED: 'uploaded',
  ERROR: 'error',
};

const FileUploadSimple = ({
  /** Floating label / placeholder text */
  label = 'Choose file',
  /** Right-aligned helper text shown below the input */
  helperText = '8MB, 3000x3000px, PDF, JPG, PNF',
  /** native <input accept> string, e.g. ".pdf,.jpg,.png" */
  accept,
  /** External error message — forces the error state */
  error: externalError = '',
  /** Whether the input is disabled */
  disabled = false,
  /**
   * Async upload handler.
   * Receives the File object; should return a Promise.
   * Resolve → uploaded state. Reject(err) → error state.
   */
  onUpload,
  /** Called when the file is cleared (X button click) */
  onClear,
  /**
   * Name of a file the parent already holds — mounts in the uploaded state
   * (e.g. returning to a form step). Read on mount only.
   */
  defaultFileName = '',
  className = '',
}) => {
  const [uploadState, setUploadState] = useState(defaultFileName ? STATES.UPLOADED : STATES.IDLE);
  const [fileName, setFileName] = useState(defaultFileName);
  const [progress, setProgress] = useState(0);
  const [internalError, setInternalError] = useState('');

  const fileInputRef = useRef(null);
  const progressTimerRef = useRef(null);

  const isIdle = uploadState === STATES.IDLE;
  const isLoading = uploadState === STATES.LOADING;
  const isUploaded = uploadState === STATES.UPLOADED;
  const isError = uploadState === STATES.ERROR || Boolean(externalError);
  const hasFile = !isIdle;

  const errorMessage = externalError || internalError;

  /* ── Progress simulation ─────────────────────────────────────────────────── */

  const clearProgressTimer = useCallback(() => {
    if (progressTimerRef.current) {
      clearTimeout(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  }, []);

  const simulateProgress = useCallback(
    (file) => {
      setUploadState(STATES.LOADING);
      setProgress(0);

      let current = 0;

      const tick = () => {
        // Fast increments for "instant" feel — 4-5 steps
        current += Math.random() * 30 + 20;
        const next = Math.min(current, 90); // pause at 90% until upload resolves
        setProgress(next);

        if (next < 90) {
          progressTimerRef.current = setTimeout(tick, 60);
        }
      };

      progressTimerRef.current = setTimeout(tick, 40);

      // Kick off actual upload if callback provided
      if (onUpload) {
        onUpload(file)
          .then(() => {
            clearProgressTimer();
            setProgress(100);
            setTimeout(() => {
              setUploadState(STATES.UPLOADED);
              setProgress(0);
            }, 250);
          })
          .catch((err) => {
            clearProgressTimer();
            setProgress(0);
            setUploadState(STATES.ERROR);
            setInternalError(err?.message || 'Upload failed');
          });
      } else {
        // No callback — simulate to 100% and mark uploaded
        const finish = () => {
          clearProgressTimer();
          setProgress(100);
          setTimeout(() => {
            setUploadState(STATES.UPLOADED);
            setProgress(0);
          }, 250);
        };
        progressTimerRef.current = setTimeout(finish, 400);
      }
    },
    [onUpload, clearProgressTimer],
  );

  /* ── Handlers ────────────────────────────────────────────────────────────── */

  const handleFileChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setFileName(file.name);
      setInternalError('');
      simulateProgress(file);
    },
    [simulateProgress],
  );

  const handleClear = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      clearProgressTimer();
      setUploadState(STATES.IDLE);
      setFileName('');
      setProgress(0);
      setInternalError('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      onClear?.();
    },
    [clearProgressTimer, onClear],
  );

  const openPicker = useCallback(() => {
    if (!disabled) fileInputRef.current?.click();
  }, [disabled]);

  const handleWrapperClick = useCallback(() => {
    // Only open picker if idle (not loading/uploaded/error)
    if (isIdle) openPicker();
  }, [isIdle, openPicker]);

  const handleWrapperKeyDown = useCallback(
    (e) => {
      if ((e.key === 'Enter' || e.key === ' ') && isIdle) {
        e.preventDefault();
        openPicker();
      }
    },
    [isIdle, openPicker],
  );

  const handleButtonClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (isLoading || isUploaded) {
        handleClear(e);
      } else {
        // idle or error → open picker
        openPicker();
      }
    },
    [isLoading, isUploaded, handleClear, openPicker],
  );

  /* ── Derived classes ─────────────────────────────────────────────────────── */

  const fieldModifier = isError
    ? 'fu-simple__field--error'
    : isLoading
    ? 'fu-simple__field--loading'
    : isUploaded
    ? 'fu-simple__field--uploaded'
    : '';

  return (
    <div className={`fu-simple ${isError ? 'fu-simple--error' : ''} ${helperText ? 'fu-simple--has-notes' : ''} ${className}`}>
      {/* Hidden native file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        className="fu-simple__native-input"
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
      />

      {/* Visual input */}
      <div
        className={`fu-simple__field ${fieldModifier} ${disabled ? 'fu-simple__field--disabled' : ''}`}
        onClick={handleWrapperClick}
        onKeyDown={handleWrapperKeyDown}
        role={isIdle ? 'button' : undefined}
        tabIndex={isIdle && !disabled ? 0 : undefined}
        aria-label={isIdle ? label : undefined}
      >
        {/* Content row */}
        <div className="fu-simple__content">
          {/* Left icon */}
          <div className="fu-simple__icon">
            <Image size={20} />
          </div>

          {/* Label + value */}
          <div className="fu-simple__label-area">
            <span className={`fu-simple__placeholder ${hasFile ? 'fu-simple__placeholder--label' : ''}`}>
              {label}
            </span>
            {hasFile && (
              <span className="fu-simple__value">{fileName}</span>
            )}
          </div>

          {/* Right action button */}
          <div className="fu-simple__right-ui">
            <Button
              size="tertiary"
              variant="black"
              iconOnly
              iconLeft={isLoading || isUploaded ? <X size={20} /> : <Upload size={20} />}
              className="fu-simple__btn"
              onClick={handleButtonClick}
              type="button"
              disabled={disabled}
              tabIndex={0}
              aria-label={isLoading || isUploaded ? 'Remove file' : 'Upload file'}
            />
          </div>
        </div>

        {/* Progress bar — visible during loading */}
        {isLoading && (
          <div className="fu-simple__progress-track">
            <div
              className="fu-simple__progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>

      {/* Error tooltip — absolutely positioned, does not affect layout height */}
      {errorMessage && (
        <div className="fu-simple__error" role="alert">
          {errorMessage}
        </div>
      )}

      {/* Helper notes — always in normal flow */}
      {helperText && (
        <p className="fu-simple__notes">{helperText}</p>
      )}
    </div>
  );
};

export default FileUploadSimple;
